import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildMakeModelDefaultYear,
  buildModelFamilyIdsModelYearsPath,
  VehicleModel,
  VehicleEntities,
} from 'client/data/models/vehicle-v2';
import { VehicleGraphqlEntities } from 'client/data/models/vehicle-graphql';
import { getFilteredModelYears, getModelYearsWithReview } from 'site-modules/shared/utils/core-page/model-year-helpers';
import { getCurrentYear } from 'site-modules/shared/utils/time-util';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { YearLinksCarousel } from './year-links-carousel/year-links-carousel';
import { YearLinksList } from './year-links-list/year-links-list';

const YEARS_LIMIT = 3;

function OtherReviewYearsUI({
  vehicle: {
    year,
    model: { name: modelName },
    make: { name: makeName },
  },
  modelYears = null,
  defaultYear = null,
  className = null,
  heading = null,
  isMobile = false,
  hasSpeculationRule = false,
}) {
  const allModelYears = useMemo(() => {
    const currentYear = parseInt(getCurrentYear(), 10);
    return getModelYearsWithReview(
      getFilteredModelYears(modelYears, modelName).filter(
        ({ year: modelYear }) => parseInt(modelYear, 10) > currentYear - YEARS_LIMIT
      ),
      defaultYear
    );
  }, [modelYears, modelName, defaultYear]);

  if (allModelYears?.length < 2) {
    return null;
  }

  return (
    <Experiment name="core-7872-other-year-carousel" showDefault>
      <Recipe name="ctrl" isDefault>
        <YearLinksList
          years={allModelYears}
          currentYear={year}
          makeName={makeName}
          modelName={modelName}
          className={classnames('bg-cool-gray-90 rounded-12 px-1 py-1', className)}
          heading={heading}
          headingClassName="mb-0_75"
          isMobile={isMobile}
          hasSpeculationRule={hasSpeculationRule}
        />
      </Recipe>
      <Recipe name="chal">
        <YearLinksCarousel
          years={allModelYears}
          currentYear={year}
          className={classnames('bg-cool-gray-90 rounded-12 px-0_5 px-md-1 py-1', className)}
          heading={heading}
          headingClassName="size-20 fw-bold mb-1_5"
          hasSpeculationRule={hasSpeculationRule}
        />
      </Recipe>
    </Experiment>
  );
}

OtherReviewYearsUI.propTypes = {
  vehicle: VehicleGraphqlEntities.Vehicle.isRequired,
  modelYears: PropTypes.arrayOf(VehicleEntities.ModelYearByFamilyId),
  defaultYear: PropTypes.number,
  className: PropTypes.string,
  heading: PropTypes.string,
  isMobile: PropTypes.bool,
  hasSpeculationRule: PropTypes.bool,
};

export const OtherReviewYears = connectToModel(OtherReviewYearsUI, {
  defaultYear: bindToPath(
    ({ vehicle }) => buildMakeModelDefaultYear({ make: vehicle.make.slug, model: vehicle.model.slug }),
    VehicleModel
  ),
  modelYears: bindToPath(({ vehicle }) => buildModelFamilyIdsModelYearsPath(vehicle.modelFamilyIds), VehicleModel),
});
